import { inject, Inject, Injectable } from "@angular/core";
import { ENVIRONMENT } from "../environments/environments.token";
import { Environment } from "../environments/environment.interface";
import { Cls_UserLogin, Cls_GetOTPMaster, Cls_ChangeUserPassword, Cls_VerifyOTPMaster } from "../models";
import { GlobalService } from "./global.service";
import { map } from "rxjs";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class UserManagementService {
  private readonly httpClient = inject(HttpClient);
  private readonly globalService = inject(GlobalService);
  private env: Environment = inject(ENVIRONMENT);

  constructor() {
    this.USER_API = this.env.USER_API || "";
  }

  private readonly USER_API: string = "";

  loginObject(res: any) {
    const { Menus } = res;
    Menus.map((el: any) => {
      el.pageName = el.Name;
      el.pageUrl = el.URL;
      el.isIcon = el.IsIcon;
      el.iconName = el.IconName?.toLowerCase();
      el.iconClass = el.IconName;
      el.defaultSvgIcon = el.DefaultSvg;
      el.selectedSvgIcon = el.SelectedSvg;
    });
    const menus = this.globalService.generateRecursion(Menus, "MenuID", "ParentMenuID");
    this.globalService.orderNestedStructure(menus, "Order");
    res.NestedMenu = menus;
  }

  //#region POST

  UserLogIn(bodyData: Cls_UserLogin) {
    return this.httpClient.post(`${this.USER_API}/api/account/login`, bodyData).pipe(
      map((res: any) => {
        const userInfo = res;
        this.loginObject(userInfo);
        return userInfo;
      })
    );
  }

  UserLogInUsingToken() {
    const appName: string = this.env.APP_NAME || "";
    const companyName: string = this.env.COMPANY_NAME || "";
    return this.httpClient.get(`${this.USER_API}/api/account/user/details?appName=${appName}&companyName=${companyName}`).pipe(
      map((res: any) => {
        const userInfo = res;
        this.loginObject(userInfo);
        return userInfo;
      })
    );
  }

  InsertUpdateUserTableColumn(bodyData: any) {
    return this.httpClient.post(`${this.USER_API}/api/account/InsertUpdateUserTableColumn`, bodyData);
  }

  GetOTP(bodyData: Cls_GetOTPMaster) {
    return this.httpClient.post(`${this.USER_API}/api/master/GetOTP`, bodyData);
  }

  VerifyOTP(bodyData: Cls_VerifyOTPMaster) {
    return this.httpClient.post(`${this.USER_API}/api/master/VerifyOTP`, bodyData);
  }

  //#endregion

  UpdateUserInfo(bodyData: any) {
    return this.httpClient.put(`${this.USER_API}/api/account/UpdateUserInfo`, bodyData);
  }

  UpdateUserPassword(bodyData: any) {
    return this.httpClient.put(`${this.USER_API}/api/account/UpdateUserPassword`, bodyData);
  }

  UpdateUserPasswordUsingOTP(bodyData: Cls_ChangeUserPassword) {
    return this.httpClient.put(`${this.USER_API}/api/account/UpdateUserPasswordUsingOTP`, bodyData);
  }

  //#region PUT

  //#endregion

  //#region GET

  //#region Account

  GetUserApplicationsByUID() {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserApplicationsByUID`);
  }

  GetUsersByRID(RID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUsersByRID?RID=${RID}`);
  }

  GetAllUserRolesByAID(AID: number, IsAll: boolean, APP_NAME: string) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetAllUserRolesByAID?AID=${AID}&IsAll=${IsAll}&APP_NAME=${APP_NAME}`);
  }

  GetUserRoleByID(RID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserRoleByID?RID=${RID}`);
  }

  UpdateUserID(ID: string, UID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/UpdateUserID?ID=${ID}&UID=${UID}`);
  }

  GetUserRolesByAIDs(AIDs: string) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserRolesByAIDs?AID=${AIDs}`);
  }

  GetUserMenusByRoleIDs(RIDs: string, AID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserMenusByRoleIDs?RID=${RIDs}&AID=${AID}`);
  }

  GetUsersByCID(CID: number, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUsersByCID?CID=${CID}&IsAll=${IsAll}`);
  }

  GetUserByUID(UID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserByUID?UID=${UID}`);
  }

  GetUserProfile() {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUserProfile`);
  }

  GetUsersByRIDv2(RID: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUsersByRIDv2?RID=${RID}&companyName=${this.env.COMPANY_NAME}&appName=${this.env.APP_NAME}`);
  }

  GetUsersBySearch(Search: string, Limit: number) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetUsersBySearch?AppName=${this.env.APP_NAME}&CompanyName=${this.env.COMPANY_NAME}&Search=${Search}&Limit=${Limit}`);
  }

  //#endregion

  //#region Master

  GetTableColumnsByName(TableName: string, AID: number, APP_NAME: string) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetTableColumnsByName?TableName=${TableName}&AID=${AID}&APP_NAME=${APP_NAME}`);
  }

  GetApplicationByAID(AID: number, APP_NAME?: string) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetApplicationByAID?AID=${AID}&APP_NAME=${APP_NAME}`);
  }

  GetOrganizationByID(OID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetOrganizationByID?OID=${OID}`);
  }

  GetMenuByID(MenuID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetMenuByID?MenuID=${MenuID}`);
  }

  GetMenusByAID(AID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetMenusByAID?AID=${AID}`);
  }

  GetAllMenus() {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllMenus`);
  }

  GetAllApplication(IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllApplication?IsAll=${IsAll}`);
  }

  GetCompanyByCID(CID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetCompanyByCID?CID=${CID}`);
  }

  GetAllCompany(IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllCompany?IsAll=${IsAll}`);
  }

  GetAllCountry(IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllCountry?IsAll=${IsAll}`);
  }

  GetCountryByID(CountryID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetCountryByID?CountryID=${CountryID}`);
  }

  GetStateByID(StateID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetStateByID?StateID=${StateID}`);
  }

  GetStateByCountryID(CountryID: number, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetStateByCountryID?CountryID=${CountryID}&IsAll=${IsAll}`);
  }

  GetCityByID(CityID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetCityByID?CityID=${CityID}`);
  }

  GetCityByStateID(StateID: number, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetCityByStateID?StateID=${StateID}&IsAll=${IsAll}`);
  }

  GetLinkedApplicationWithCompany(CID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetLinkedApplicationWithCompany?CID=${CID}`);
  }

  GetAllTableColumnsByAID(AID: number) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllTableColumnsByAID?AID=${AID}`);
  }

  GetAllMasterTypes(IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetAllMasterTypes?IsAll=${IsAll}`);
  }

  GetUserProfileConfiguration() {
    return this.httpClient.get(`${this.USER_API}/api/master/GetUserProfileConfiguration`);
  }

  GetCountryWithPhoneCodes() {
    return this.httpClient.get(`${this.USER_API}/api/countries-with-phone-codes`);
  }

  GetMasterTypeValueByTID(MTID: number, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetMasterTypeValueByTID?MTID=${MTID}&IsAll=${IsAll}`);
  }

  GetMasterTypeValueByTIDs(MTID: string, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/master/GetMasterTypeValueByTIDs?MTID=${MTID}&IsAll=${IsAll}`);
  }

  GetCustomerTypeByCID(CID: string | number, IsAll: boolean) {
    return this.httpClient.get(`${this.USER_API}/api/account/GetCustomerTypesByCompany?CID=${CID}&IsAll=${IsAll}`);
  }

  //#endregion

  //#endregion

  //#region DELETE

  //#endregion
}
